/**
 * RunPlanList — the deep agent's plan (write_todos) rendered as a checklist.
 *
 * Shown live while a run is in progress (RunningProgressPanelView, fed from
 * SSE `plan` events). `coercePlanTodos` validates the raw payload.
 */

import { Check, Circle, ListTodo, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { HypothesisRunTodo } from '@/types/hypothesis-finding'
import { cn } from '@/utils/cn'

const TODO_STATUSES: ReadonlySet<string> = new Set(['pending', 'in_progress', 'completed'])

/** Coerce a raw write_todos array (from an SSE `plan` event) into validated
 * todos. */
export function coercePlanTodos(raw: unknown): HypothesisRunTodo[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap(item => {
    if (typeof item !== 'object' || item === null) return []
    const { content, status } = item as { content?: unknown; status?: unknown }
    if (typeof content !== 'string' || !content) return []
    const safeStatus =
      typeof status === 'string' && TODO_STATUSES.has(status)
        ? (status as HypothesisRunTodo['status'])
        : 'pending'
    return [{ content, status: safeStatus }]
  })
}

function TodoStatusIcon({ status }: { status: HypothesisRunTodo['status'] }) {
  if (status === 'completed') return <Check className="size-3.5 shrink-0 text-primary" />
  if (status === 'in_progress')
    return <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
  return <Circle className="size-3 shrink-0 text-muted-foreground/60" />
}

export function RunPlanList({ todos }: { todos: HypothesisRunTodo[] }) {
  const { t } = useTranslation()
  if (todos.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <ListTodo className="size-3" />
        {t('hypothesisRun.plan')}
      </div>
      <ul className="flex flex-col gap-1 text-xs">
        {todos.map((todo, i) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: ephemeral list, no stable id
            key={`${todo.content}-${i}`}
            className="flex items-start gap-2"
          >
            <span className="mt-0.5 shrink-0">
              <TodoStatusIcon status={todo.status} />
            </span>
            <span
              className={cn(
                'min-w-0 break-words',
                todo.status === 'completed'
                  ? 'text-muted-foreground line-through'
                  : todo.status === 'in_progress'
                    ? 'font-medium text-foreground'
                    : 'text-foreground/80'
              )}
            >
              {todo.content}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
